"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Field = void 0;
const model_1 = require("../model");
/**
 * Field operations and information collections for datasheet
 */
class Field {
    /**
     * @hidden
     */
    constructor(datasheetId, wCtx, fieldData) {
        this.datasheetId = datasheetId;
        this.wCtx = wCtx;
        this.fieldData = fieldData;
        this.modelField = new model_1.Field(this.datasheetId, this.wCtx, this.fieldData);
    }
    /**
     * Field id, unique identification of the field
     * @returns
     *
     * #### Example
     * ```js
     * console.log(myField.id); // => 'fldxxxxxx'
     * ```
     */
    get id() {
        return this.modelField.id;
    }
    /**
     * Field name, different field names are called non-repeating values
     *
     * @returns
     *
     * #### Example
     * ```js
     * console.log(myField.name); // => 'FieldName'
     * ```
     */
    get name() {
        return this.modelField.name;
    }
    /**
     * Field types, which are enumerated values, can be found in {@link FieldType}
     *
     * @returns
     *
     * #### Example
     * ```js
     * console.log(myField.type); // => 'SingleLineText'
     * ```
     */
    get type() {
        return this.modelField.type;
    }
    /**
     * Returns the description of the field
     *
     * @returns
     *
     * #### Example
     * ```js
     * console.log(myField.description);
     * // => 'This is my field'
     * ```
     */
    get description() {
        return this.modelField.description;
    }
    /**
     *
     * Returns the property of the field, which is different for different types of fields
     * Returns null means that the field has no properties configured
     * Refer to {@link FieldType}
     *
     * @return {@link FieldType}
     *
     * #### Example
     * ```js
     * console.log(myField.property.symbol); // => '￥'
     * ```
     */
    get property() {
        return this.modelField.property;
    }
    /**
     * Determine if the current field is a "computed field"
     * "Computed fields" means the types of fields that do not allow the user to actively write values.
     * (e.g., auto number, formula, link, modification time, creation time, modifier, creator)
     *
     * @returns
     *
     * #### Example
     * ```js
     * console.log(mySingleLineTextField.isComputed);
     * // => false
     * console.log(myAutoNumberField.isComputed);
     * // => true
     * ```
     */
    get isComputed() {
        return this.modelField.isComputed;
    }
    /**
     * Returns whether the current field belongs to the primary field, which in datasheet is always the field where the first field is located.
     *
     * @returns
     *
     * #### Example
     * ```js
     * console.log(myField.isPrimary); // => true
     * ```
     */
    get isPrimary() {
        return this.modelField.isPrimary;
    }
    /**
     * Update the description of the field.
     *
     * Throws an error if the user does not have permission to update the field, or if an invalid description is provided.
     *
     * @param description new description for the field
     * @returns
     *
     * #### Example
     * ```js
     *  await field.updateDescriptionAsync('this is a new description')
     * ```
     */
    updateDescriptionAsync(description) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.modelField.updateDescription(description);
        });
    }
    /**
     *
     * Updates the property for this field,
     * tips: that the update property configuration must be overwritten in full.
     *
     * Throws an error if the user does not have permission to update the field,
     * if invalid property are provided, if this field has no writable property, or if updates to this field type is not supported.
     *
     * Refer to {@link FieldType} for supported field types, the write format for property, and other specifics for certain field types.
     *
     * @param property new property for the field.
     * @param options optional options to affect the behavior of the update.
     * @returns
     *
     * #### Example
     * ```js
     * function addOptionToSelectField(selectField, nameForNewOption) {
     *   const updatedOptions = {
     *     options: [
     *       ...selectField.options.choices,
     *       {name: nameForNewOption},
     *     ]
     *   };
     *
     *   await selectField.updatePropertyAsync(updatedOptions);
     * }
     * ```
     */
    updatePropertyAsync(property, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.modelField.updateProperty(property, options);
        });
    }
}
exports.Field = Field;
